"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Timeline, DataSet } from "vis-timeline/standalone";
import "vis-timeline/styles/vis-timeline-graph2d.min.css";

import { Slider } from "../ui/Slider";
import { epochToFormattedDateTime } from "../../lib/utils";
import InsightsPopUp from "./InsightsPopUp";
import { InsightEvent, LabelMapping, ProcessedInsight } from "./types";
import {
  buildDefaultSuppressed,
  getDisplayName,
  processInsights,
} from "./utils";

const TIME_INTERVAL_MS = 60000;
const MIN_VISIBLE_MS = 1000;

function computePaddedRange(min: number, max: number) {
  const span = max - min;
  if (span <= 0) {
    return { min, max, span: 0 };
  }
  const pad = Math.min(5000, Math.max(200, Math.floor(span * 0.02)));
  return {
    min: min - pad,
    max: max + pad,
    span: span + 2 * pad,
  };
}

interface InsightsTimelineProps {
  data: InsightEvent[];
  labelMappings: LabelMapping[];
}

const InsightsTimeline: React.FC<InsightsTimelineProps> = ({
  data,
  labelMappings,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<Timeline | null>(null);
  const insightMapRef = useRef<Map<string, ProcessedInsight>>(new Map());
  const zoomRef = useRef(3);

  const [zoom, setZoom] = useState(3);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedInsight, setSelectedInsight] =
    useState<ProcessedInsight | null>(null);

  const defaultSuppressed = useMemo(
    () => buildDefaultSuppressed(labelMappings),
    [labelMappings]
  );
  const [enabledSuppressed, setEnabledSuppressed] = useState<string[]>([]);

  const activeSuppressed = useMemo(() => {
    const enabled = new Set(enabledSuppressed);
    return new Set([...defaultSuppressed].filter((n) => !enabled.has(n)));
  }, [defaultSuppressed, enabledSuppressed]);

  const { insights, timeRange, groups } = useMemo(
    () => processInsights(data, labelMappings, activeSuppressed),
    [data, labelMappings, activeSuppressed]
  );

  const totalDuration = timeRange.max - timeRange.min;

  const paddedRange = useMemo(
    () => computePaddedRange(timeRange.min, timeRange.max),
    [timeRange.min, timeRange.max]
  );

  zoomRef.current = zoom;

  const applyZoomWindow = (timeline: Timeline, range = paddedRange) => {
    if (range.span <= 0) return;
    const visibleMs = Math.min(range.span, range.span / zoomRef.current);
    timeline.setWindow(
      new Date(range.min),
      new Date(range.min + visibleMs),
      { animation: false }
    );
  };

  useEffect(() => {
    if (!containerRef.current || insights.length === 0) return;

    const map = new Map<string, ProcessedInsight>();
    for (const insight of insights) {
      map.set(insight.id, insight);
    }
    insightMapRef.current = map;

    const rangeMin = new Date(paddedRange.min);
    const rangeMax = new Date(paddedRange.max);
    const initialVisibleMs = Math.min(
      paddedRange.span,
      paddedRange.span / zoomRef.current
    );
    const initialEnd = new Date(paddedRange.min + initialVisibleMs);

    const visGroups = new DataSet(
      groups.map((g) => ({
        id: g.id,
        content: g.label,
      }))
    );

    const visItems = new DataSet(
      insights.map((insight) => {
        const bg = insight.color;
        const instantStyle = insight.isInstant
          ? `repeating-linear-gradient(45deg, ${bg}, ${bg} 5px, rgba(255,255,255,0.35) 5px, rgba(255,255,255,0.35) 10px)`
          : bg;

        return {
          id: insight.id,
          group: insight.artifact,
          start: new Date(insight.startTime),
          end: new Date(insight.endTime),
          content: "",
          style: insight.isInstant
            ? `background: ${instantStyle}; border-color: ${bg};`
            : `background-color: ${bg}; border-color: ${bg};`,
          title: [
            getDisplayName(insight.artifact, labelMappings),
            `Start: ${epochToFormattedDateTime(insight.startTime.toString())}`,
            insight.isInstant
              ? `Duration: instant (${insight.duration}ms min bar)`
              : `Duration: ${insight.time_taken_ms}ms`,
            insight.errors.length ? `Errors: ${insight.errors.join(", ")}` : "",
            insight.warnings.length
              ? `Warnings: ${insight.warnings.join(", ")}`
              : "",
          ]
            .filter(Boolean)
            .join("\n"),
        };
      })
    );

    if (timelineRef.current) {
      timelineRef.current.destroy();
      timelineRef.current = null;
    }

    const timeline = new Timeline(
      containerRef.current,
      visItems,
      visGroups,
      {
        stack: false,
        horizontalScroll: true,
        zoomKey: "ctrlKey",
        orientation: { axis: "top", item: "top" },
        showCurrentTime: false,
        margin: { item: 8, axis: 8 },
        min: rangeMin,
        max: rangeMax,
        start: rangeMin,
        end: initialEnd,
        zoomMin: MIN_VISIBLE_MS,
        zoomMax: Math.max(MIN_VISIBLE_MS, paddedRange.span),
        moveable: true,
        zoomable: true,
        groupOrder: (a: { id: string }, b: { id: string }) => {
          const ai = groups.findIndex((g) => g.id === a.id);
          const bi = groups.findIndex((g) => g.id === b.id);
          return ai - bi;
        },
        format: {
          minorLabels: {
            millisecond: "SSS",
            second: "s",
            minute: "m",
            hour: "H",
          },
          majorLabels: {
            millisecond: "HH:mm:ss",
            second: "HH:mm:ss",
            minute: "HH:mm",
            hour: "ddd D",
          },
        },
      }
    );

    timeline.on("select", (props: { items: string[] }) => {
      const id = props.items?.[0];
      if (!id) return;
      const insight = insightMapRef.current.get(id);
      if (insight) {
        setSelectedInsight(insight);
        setShowPopup(true);
      }
    });

    const clampWindow = () => {
      const win = timeline.getWindow();
      const winStart = win.start.getTime();
      const winEnd = win.end.getTime();
      const winSpan = winEnd - winStart;
      const maxSpan = paddedRange.span;

      let nextStart = winStart;
      let nextEnd = winEnd;

      if (winSpan >= maxSpan) {
        nextStart = paddedRange.min;
        nextEnd = paddedRange.max;
      } else {
        if (nextStart < paddedRange.min) {
          nextStart = paddedRange.min;
          nextEnd = nextStart + winSpan;
        }
        if (nextEnd > paddedRange.max) {
          nextEnd = paddedRange.max;
          nextStart = nextEnd - winSpan;
        }
      }

      if (nextStart !== winStart || nextEnd !== winEnd) {
        timeline.setWindow(new Date(nextStart), new Date(nextEnd), {
          animation: false,
        });
      }
    };

    timeline.on("rangechange", clampWindow);
    timeline.on("rangechanged", clampWindow);

    timelineRef.current = timeline;
    applyZoomWindow(timeline);

    const resizeObserver = new ResizeObserver(() => {
      timeline.redraw();
    });
    resizeObserver.observe(containerRef.current);

    const rafId = requestAnimationFrame(() => {
      applyZoomWindow(timeline);
      timeline.redraw();
    });

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      timeline.off("rangechange", clampWindow);
      timeline.off("rangechanged", clampWindow);
      timeline.destroy();
      timelineRef.current = null;
    };
  }, [insights, groups, labelMappings, paddedRange]);

  useEffect(() => {
    if (!timelineRef.current || paddedRange.span <= 0) return;
    applyZoomWindow(timelineRef.current);
    timelineRef.current.redraw();
  }, [zoom, paddedRange]);

  if (!data?.length) {
    return (
      <div className="text-sm text-neutral-600">No insight events found.</div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="text-sm text-neutral-600">
        All event types are hidden. Enable suppressed types below to view data.
      </div>
    );
  }

  const intervalCount = Math.ceil(totalDuration / TIME_INTERVAL_MS) + 1;

  return (
    <div className="relative text-sm insights-timeline">
      <div className="mb-4 flex justify-between items-center flex-wrap gap-4">
        <div className="text-sm shadow-md p-4 rounded-lg">
          <div className="text-lg font-bold">Overview</div>
          <p>Start Time: {epochToFormattedDateTime(timeRange.min.toString())}</p>
          <p>End Time: {epochToFormattedDateTime(timeRange.max.toString())}</p>
        </div>

        <div className="flex items-center space-x-4 w-64 border-2 p-2">
          <p className="font-bold text-neutral-500">Zoom</p>
          <span className="w-12 text-sm">{(zoom * 100).toFixed(0)}%</span>
          <Slider
            value={[zoom]}
            onValueChange={(val) => setZoom(val[0])}
            min={1}
            max={20}
            step={0.1}
            className="w-full cursor-pointer"
          />
        </div>
      </div>

      {defaultSuppressed.size > 0 && (
        <div className="mb-3">
          <div className="text-sm text-neutral-700 mb-1">
            Hidden event types (enable to show):
          </div>
          <div className="flex flex-wrap gap-3">
            {Array.from(defaultSuppressed).map((name) => {
              const isEnabled = enabledSuppressed.includes(name);
              return (
                <label
                  key={`sup-${name}`}
                  className="inline-flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() =>
                      setEnabledSuppressed((prev) =>
                        isEnabled
                          ? prev.filter((x) => x !== name)
                          : [...prev, name]
                      )
                    }
                  />
                  <span className="text-neutral-800">
                    {getDisplayName(name, labelMappings)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="border rounded-lg overflow-hidden bg-white">
        <div
          ref={containerRef}
          style={{
            minHeight: Math.max(groups.length * 48 + 80, 200),
          }}
        />
        <div className="px-4 pb-3 text-neutral-400 font-bold text-xs">
          ({TIME_INTERVAL_MS / 60000} min) Interval · {intervalCount} ticks
          across timeline
        </div>
      </div>

      {showPopup && selectedInsight && (
        <InsightsPopUp
          data={selectedInsight}
          labelMappings={labelMappings}
          onClose={() => {
            setShowPopup(false);
            setSelectedInsight(null);
          }}
        />
      )}

      <style>{`
        .insights-timeline .vis-labelset .vis-label {
          border: 1px solid #000;
          font-size: 13px;
          min-width: 220px;
        }
        .insights-timeline .vis-item {
          border-width: 1px;
          border-radius: 4px;
          cursor: pointer;
        }
        .insights-timeline .vis-item.vis-selected {
          border-color: #1e293b;
          box-shadow: 0 0 0 2px rgba(30, 41, 59, 0.25);
        }
        .insights-timeline .vis-time-axis .vis-text {
          font-size: 11px;
        }
      `}</style>
    </div>
  );
};

export default InsightsTimeline;
